/**
 * File Handle Manager
 * Manages FileSystemFileHandle objects for recent documents
 * Note: File handles cannot be stored in localStorage, so they only persist during the session
 */

import { logger } from './logger';

// Type definitions for File System Access API (not fully in TypeScript DOM types yet)
type FileSystemPermissionMode = 'read' | 'readwrite';
type PermissionState = 'granted' | 'denied' | 'prompt';

interface FileSystemHandlePermissionDescriptor {
  mode?: FileSystemPermissionMode;
}

// Extend FileSystemFileHandle with permission methods
interface FileSystemFileHandleExtended extends FileSystemFileHandle {
  queryPermission(
    descriptor?: FileSystemHandlePermissionDescriptor
  ): Promise<PermissionState>;
  requestPermission(
    descriptor?: FileSystemHandlePermissionDescriptor
  ): Promise<PermissionState>;
}

export interface FileHandleStats {
  totalHandles: number;
  isSupported: boolean;
}

class FileHandleManager {
  private fileHandles: Map<string, FileSystemFileHandle>;
  private isSupported: boolean;

  constructor() {
    // In-memory storage for file handles (session only)
    this.fileHandles = new Map();

    // Check if File System Access API is supported
    this.isSupported =
      typeof window !== 'undefined' &&
      'showOpenFilePicker' in window &&
      'FileSystemFileHandle' in window;
  }

  /**
   * Check if File System Access API is supported in this browser
   * @returns True if supported
   */
  isFileSystemAccessSupported(): boolean {
    return this.isSupported;
  }

  /**
   * Store a file handle for a document
   * @param docId - Document ID from DocumentHistory
   * @param fileHandle - File handle to store
   */
  setFileHandle(docId: string, fileHandle: FileSystemFileHandle): void {
    if (!this.isSupported) {
      logger.warn('File System Access API not supported');
      return;
    }

    if (!fileHandle || !fileHandle.kind || fileHandle.kind !== 'file') {
      logger.warn('Invalid file handle provided');
      return;
    }

    this.fileHandles.set(docId, fileHandle);
    logger.debug(`Stored file handle for document: ${docId}`);
  }

  /**
   * Get file handle for a document
   * @param docId - Document ID
   * @returns File handle or null if not found
   */
  getFileHandle(docId: string): FileSystemFileHandle | null {
    return this.fileHandles.get(docId) || null;
  }

  /**
   * Check if a file handle exists for a document
   * @param docId - Document ID
   * @returns True if file handle exists
   */
  hasFileHandle(docId: string): boolean {
    return this.fileHandles.has(docId);
  }

  /**
   * Remove file handle for a document
   * @param docId - Document ID
   */
  removeFileHandle(docId: string): void {
    this.fileHandles.delete(docId);
  }

  /**
   * Get File object from file handle
   * @param docId - Document ID
   * @returns File object or null if not available
   */
  async getFile(docId: string): Promise<File | null> {
    if (!this.isSupported) {
      return null;
    }

    const handle = this.getFileHandle(docId);
    if (!handle) {
      return null;
    }

    try {
      // Request permission if needed
      const permission = await this.verifyPermission(handle, true);
      if (!permission) {
        logger.warn('File access permission denied');
        return null;
      }

      // Get the file
      const file = await handle.getFile();
      return file;
    } catch (error) {
      logger.error('Error getting file from handle:', error);
      // File may have been moved or deleted
      this.removeFileHandle(docId);
      return null;
    }
  }

  /**
   * Verify permission to access a file handle
   * @param fileHandle - File handle to check
   * @param readWrite - Whether to request read-write permission
   * @returns True if permission granted
   */
  async verifyPermission(
    fileHandle: FileSystemFileHandle,
    readWrite: boolean = false
  ): Promise<boolean> {
    const options: FileSystemHandlePermissionDescriptor = {};
    if (readWrite) {
      options.mode = 'readwrite';
    }

    try {
      const handle = fileHandle as FileSystemFileHandleExtended;

      // Check if permission was already granted
      if ((await handle.queryPermission(options)) === 'granted') {
        return true;
      }

      // Request permission
      if ((await handle.requestPermission(options)) === 'granted') {
        return true;
      }

      return false;
    } catch (error) {
      logger.error('Error verifying permission:', error);
      return false;
    }
  }
}

// Export to window for debugging (dev only)
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.FileHandleManager = FileHandleManager;
}

export default FileHandleManager;
